import React, { useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, useLocation, useNavigationType, Navigate, Outlet, Link } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import ComingSoon from "@/pages/ComingSoon";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import Navbar from "@/components/Navbar";
import AuthCallback from "@/components/AuthCallback";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Opportunities from "@/pages/Opportunities";
import OpportunityDetail from "@/pages/OpportunityDetail";
import PostOpportunity from "@/pages/PostOpportunity";
import Privacy from "@/pages/Privacy";
import { InstallPrompt } from "@/components/InstallPrompt";
import CookieConsent from "@/components/CookieConsent";
import { FeedbackWidget } from "@/components/FeedbackWidget";
import { VerifyEmailBanner } from "@/components/VerifyEmailBanner";
import { FirstApplicationPromptHost } from "@/components/BringAFriendDialog";
import PageviewBeacon from "@/components/PageviewBeacon";
import PwaInstallPing from "@/components/PwaInstallPing";
import LogHours from "@/pages/LogHours";
import Messages from "@/pages/Messages";
import CalendarPage from "@/pages/CalendarPage";
import Certificate from "@/pages/Certificate";
import PublicProfile from "@/pages/PublicProfile";
import Analytics from "@/pages/Analytics";
import OrgImpact from "@/pages/OrgImpact";
import PublicOrg from "@/pages/PublicOrg";
import Teams from "@/pages/Teams";
import Admin from "@/pages/Admin";
import Discover from "@/pages/Discover";
import Stories from "@/pages/Stories";
import Partners from "@/pages/Partners";
import ForProfessionals from "@/pages/ForProfessionals";
import CampaignLanding from "@/pages/CampaignLanding";
import { CampaignBanner } from "@/components/CampaignBanner";
import Explore from "@/pages/Explore";
import Recognition from "@/pages/Recognition";
import Community from "@/pages/Community";
import CommunityPost from "@/pages/CommunityPost";
import Pathways from "@/pages/Pathways";
import Collectives from "@/pages/Collectives";
import CollectiveDetail from "@/pages/CollectiveDetail";
import PostHelpRequest from "@/pages/PostHelpRequest";
import Donate from "@/pages/Donate";
import Suspended from "@/pages/Suspended";
import About from "@/pages/About";
import Safety from "@/pages/Safety";
import VolunteeringIn from "@/pages/VolunteeringIn";
import CollectivePoster from "@/pages/CollectivePoster";
import ProgrammeGuide from "@/pages/ProgrammeGuide";
import TeamQuarterly from "@/pages/TeamQuarterly";
import NotFound from "@/pages/NotFound";
import Safeguarding from "@/pages/Safeguarding";
import ImpactArea from "@/pages/ImpactArea";
import ImpactPack from "@/pages/ImpactPack";
import VerifyEvidence from "@/pages/VerifyEvidence";
import EvidencePackPage from "@/pages/EvidencePackPage";
import Footer from "@/components/Footer";
import { captureFirstTouch } from "@/lib/attribution";

function ProtectedRoute() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-neutral-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Outlet />;
}

// Route changes always start at the top — but in-page anchors (#apply) keep
// working and back/forward (POP) lets the browser restore its own position.
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navType = useNavigationType();
  useEffect(() => {
    if (hash || navType === "POP") return;
    window.scrollTo(0, 0);
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps -- scroll reset keys on route change only
  return null;
}

function ShellRoutes() {
  const location = useLocation();
  const { user } = useAuth();
  // Detect OAuth callback synchronously
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }  // Phase 8: suspended accounts get a single full-screen view — no navbar,
  // no routes. Backend already 403s everything except /auth/me + appeals.
  if (user?.banned) {
    return <Suspended />;
  }
  return (
    <>
      <ScrollToTop />
      <Navbar />
      <CampaignBanner />
      <VerifyEmailBanner />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/opportunities" element={<Opportunities />} />
        <Route path="/opportunities/:id" element={<OpportunityDetail />} />
        <Route path="/stories" element={<Stories />} />
        {/* Phase 17-B item 2: legacy recognition routes redirect to the
            consolidated /recognition tabs (relabel, don't rename URLs). */}
        <Route path="/leaderboard" element={<Navigate to="/recognition?tab=leaderboard" replace />} />
        <Route path="/partners" element={<Partners />} />
        <Route path="/for-professionals" element={<ForProfessionals />} />
        <Route path="/campaigns/:slug" element={<CampaignLanding />} />
        {/* public: safeguarding policy (Tier 1 content, 2026-07) */}
        <Route path="/safeguarding" element={<Safeguarding />} />
        {/* public: printable A5 programme-kit guides (18-B1 item 8) */}
        <Route path="/programme-guides/:slug" element={<ProgrammeGuide />} />
        <Route path="/explore" element={<Explore />} />
        <Route path="/awards" element={<Navigate to="/recognition?tab=awards" replace />} />
        <Route path="/badges" element={<Navigate to="/recognition?tab=badges" replace />} />
        <Route path="/recognition" element={<Recognition />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/community" element={<Community />} />
        <Route path="/community/:id" element={<CommunityPost />} />
        <Route path="/pathways" element={<Pathways />} />
        <Route path="/collectives" element={<Collectives />} />
        <Route path="/collectives/:id" element={<CollectiveDetail />} />
        <Route path="/volunteering-in/:slug" element={<VolunteeringIn />} />
        <Route path="/impact/:slug" element={<ImpactArea />} />
        <Route path="/impact/:slug/pack" element={<ImpactPack />} />
        <Route path="/verify/:code" element={<VerifyEvidence />} />
        <Route path="/donate" element={<Donate />} />
        <Route path="/about" element={<About />} />
        <Route path="/safety" element={<Safety />} />
        <Route path="/u/:userId" element={<PublicProfile />} />
        <Route path="/o/:orgId/impact" element={<OrgImpact />} />
        <Route path="/o/:orgId" element={<PublicOrg />} />

        <Route element={<ProtectedRoute />}>
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/discover" element={<Discover />} />
          <Route path="/post-opportunity" element={<PostOpportunity />} />
          <Route path="/help-request/new" element={<PostHelpRequest />} />
          <Route path="/opportunities/:editId/edit" element={<PostOpportunity />} />
          <Route path="/log-hours" element={<LogHours />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/certificate/:tierId" element={<Certificate />} />
          <Route path="/collectives/:id/poster" element={<CollectivePoster />} />
          <Route path="/evidence/:id" element={<EvidencePackPage />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/teams" element={<Teams />} />
          <Route path="/teams/:id/quarterly" element={<TeamQuarterly />} />
          <Route path="/admin" element={<Admin />} />
        </Route>
        {/* Catch-all 404 with "did you mean" for common wrong route shapes
            (18-A1 follow-up, upgraded in the 18-B2 polish batch). */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      {/* /discover is a viewport-fit swipe surface on mobile — the footer
          below it would make the page scrollable and fight the gesture. */}
      <div className={location.pathname === "/discover" ? "hidden lg:block" : ""}>
        <Footer />
      </div>
    </>
  );
}

function App() {
  // Coming-soon holding mode is backend-driven (COMING_SOON env) so flipping
  // it is a single env change — no frontend rebuild. While the flag loads we
  // paint a neutral blank so the real app never flashes on a gated domain.
  const [comingSoon, setComingSoon] = useState(null);
  useEffect(() => {
    captureFirstTouch(); // 17-C item 9: first-touch attribution (no-op after first visit)
    // Boot-window resilience: retry briefly before failing open, so a gated
    // prod domain doesn't flash the normal app (and preview doesn't need a
    // manual reload) when the first fetch lands during a backend restart.
    let cancelled = false;
    const attempt = (n) => {
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/config/public`)
        .then((r) => { if (!cancelled) setComingSoon(!!r.data?.coming_soon); })
        .catch(() => {
          if (cancelled) return;
          if (n < 2) setTimeout(() => attempt(n + 1), 2500 * (n + 1));
          else setComingSoon(false);
        });
    };
    attempt(0);
    return () => { cancelled = true; };
  }, []);
  if (comingSoon === null) return <div className="min-h-screen bg-cream" />;
  if (comingSoon) {
    return (
      <HelmetProvider>
        <BrowserRouter>
          <ComingSoon />
        </BrowserRouter>
      </HelmetProvider>
    );
  }

  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <ErrorBoundary>
              <ShellRoutes />
            </ErrorBoundary>
            <Toaster richColors position="top-right" />
            <InstallPrompt />
            <CookieConsent />
            <FeedbackWidget />
            <FirstApplicationPromptHost />
            <PageviewBeacon />
            <PwaInstallPing />
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
